import pRetry from 'p-retry';
import { type StateCreator } from 'zustand';
import {
  isAudioWorkEnabled,
  runTrackedAudioWork,
  swallowAudioWorkCutoff,
  trackDetachedAudioWork,
  type AudioWorkContext,
} from '../audioWorkBarrier';
import { db } from '../db';
import { getSafeErrorFields, isSessionNotFoundError } from '../errors';
import { getPipelineMode } from '../persistenceV2';
import { type RecordingKey } from '../persistenceV2/types';
import { type BackupRecording, type SessionRecordingStore } from '../types';
import { getErrorInfo } from '../utils';
import { BackupRecorder } from './lib/BackupRecorder';
import { logger } from './lib/logger';
import { simpleRetry } from './simpleRetry';
import {
  discardBackupCaptureSlotV2,
  startBackupCaptureV2,
  stopBackupCaptureV2,
} from './v2/backupCaptureLifecycleV2';
import { type BackupCaptureRecorder } from './v2/BackupCaptureRecorder';
import { processBackupV2, type BackupDispatchOutcome } from './v2/backupV2';

export interface BackupSlice {
  backupRecorders: Record<string, BackupRecorder>;
  /** The single live v2 backup capture for this page. */
  backupCaptureSlot: { recordingId: string; recorder: BackupCaptureRecorder } | null;
  /** An outcome only on the ledger pipeline; the legacy path reports nothing. */
  processBackup: (
    backup: BackupRecording,
    context?: AudioWorkContext
  ) => Promise<BackupDispatchOutcome | void>;
  startBackupRecorder: (stream: MediaStream, recordingKey: RecordingKey) => void;
  stopBackupRecorder: (recordingId?: string) => Promise<void>;
  discardBackupRecorder: (recordingId: string) => void;
}

export const createBackupSlice: StateCreator<
  SessionRecordingStore,
  [],
  [],
  BackupSlice
> = (set, get) => ({
  backupRecorders: {},
  backupCaptureSlot: null,

  processBackup: async (backup: BackupRecording, context?: AudioWorkContext) => {
    if (getPipelineMode() === 'ledger') return processBackupV2(set, get, backup, context);
    const { sessionId, recordingId } = backup;
    const { waitForConnectivity, cleanupRecordingsTask } = get();

    try {
      await pRetry(
        async () => {
          context?.throwIfStale();
          await waitForConnectivity(context?.signal);
          context?.throwIfStale();
          const recording = await db.getRecording(sessionId, recordingId);
          if (!recording) {
            logger.warn({
              event_type: 'UPLOAD_BACKUP_RECORDING_MISSING',
              recording_id: recordingId,
            });
            return;
          }

          const backupToUpload = {
            ...backup,
            startDate: recording.startDate,
            endDate: recording.endDate!,
          };
          if (context) {
            await get().config!.uploadBackup(backupToUpload, context.signal);
          } else {
            await get().config!.uploadBackup(backupToUpload);
          }

          if (isAudioWorkEnabled(context)) {
            await db.markBackupAsProcessed(
              sessionId,
              recordingId,
              !get().config!.retainData
            );
          }
        },
        { retries: 2, shouldRetry: (err) => !isSessionNotFoundError(err) }
      );
    } catch (err) {
      if (!isAudioWorkEnabled(context)) return;
      logger.error({
        event_type: 'UPLOAD_BACKUP_FAILURE',
        details: { recordingId, ...getSafeErrorFields(err) },
      });
    }

    if (!isAudioWorkEnabled(context)) return;
    void cleanupRecordingsTask();
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [recordingId]: _, ...rest } = state.backupRecorders;
      return { backupRecorders: rest };
    });
  },

  startBackupRecorder: (stream, recordingKey) => {
    if (getPipelineMode() === 'ledger')
      return startBackupCaptureV2(set, get, stream, recordingKey);

    const { sessionId, recordingId } = recordingKey;

    const handleBackupAvailable = async (
      backup: BackupRecording,
      context: AudioWorkContext
    ) => {
      try {
        context.throwIfStale();
        await simpleRetry(() => db.addBackupRecording(backup));
        context.throwIfStale();
      } catch (err) {
        if (!context.isCurrent()) throw err;
        logger.error({
          event_type: 'ADD_BACKUP_ERROR',
          recording_id: recordingId,
          details: await getErrorInfo(err),
        });

        throw err;
      }

      context.throwIfStale();
      // Main-parity: fire-and-forget upload, tracked for the logout drain.
      // The detached upload carries the kind; the persist wrapper below stays
      // 'other' so the stop tail still waits on the fast row write.
      trackDetachedAudioWork(get().processBackup(backup, context), 'backup-upload');
    };

    const backupRecorder = new BackupRecorder({
      config: get().config!,
      stream,
      sessionId,
      recordingId,
      onBackupAvailable: (b) =>
        runTrackedAudioWork((context) => handleBackupAvailable(b, context), 'other')
          .then(() => undefined)
          .catch(swallowAudioWorkCutoff),
    });

    set((state) => ({
      backupRecorders: {
        ...state.backupRecorders,
        [recordingId]: backupRecorder,
      },
    }));
  },

  stopBackupRecorder: (recordingId?: string) => {
    if (getPipelineMode() === 'ledger') return stopBackupCaptureV2(set, get, recordingId);

    const expectedId = recordingId ?? get().activeRecordingKey?.recordingId;
    const backupRecorder = get().backupRecorders[expectedId!];
    if (backupRecorder) {
      return backupRecorder.stop();
    } else {
      logger.error({
        event_type: 'STOP_BACKUP_RECORDER_NO_BACKUP_RECORDER_FOUND',
        recording_id: expectedId,
      });

      throw new Error('Unable to find correct backup recorder to stop');
    }
  },

  discardBackupRecorder: (recordingId) => {
    const backupRecorder = get().backupRecorders[recordingId];
    if (backupRecorder) {
      backupRecorder.discard();
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [recordingId]: _, ...rest } = state.backupRecorders;
        return { backupRecorders: rest };
      });
    }
    if (get().backupCaptureSlot?.recordingId === recordingId) {
      discardBackupCaptureSlotV2(set, get);
    }
  },
});
